const storageKey = (uid = "guest") => `manucalc_3s_workspace_${uid || "guest"}`;

export const DEFAULT_WORKSPACE_PREFERENCES = {
  favorites: [],
  hiddenCategories: [],
  recentTools: [],
  completedWorkflows: [],
  healthChecks: 0,
  formulaViews: 0,
  mode: "advanced",
  threeSStages: [],
};

export const loadWorkspacePreferences = (uid) => {
  try {
    const raw = window.localStorage.getItem(storageKey(uid));
    if (!raw) return DEFAULT_WORKSPACE_PREFERENCES;
    return {
      ...DEFAULT_WORKSPACE_PREFERENCES,
      ...JSON.parse(raw),
    };
  } catch {
    return DEFAULT_WORKSPACE_PREFERENCES;
  }
};

export const saveWorkspacePreferences = (uid, preferences) => {
  const next = {
    ...DEFAULT_WORKSPACE_PREFERENCES,
    ...(preferences || {}),
  };
  window.localStorage.setItem(storageKey(uid), JSON.stringify(next));
  window.dispatchEvent(new CustomEvent("manucalc:workspace-preferences-updated", { detail: { uid: uid || "guest" } }));
  return next;
};

export const normalizeRecentTool = (entry) => {
  if (!entry) return null;
  if (typeof entry === "string") {
    return {
      id: entry,
      toolId: entry,
      name: entry,
      route: `/calculator/${entry}`,
      type: "calculator",
      lastUsedAt: "",
      usageCount: 1,
    };
  }

  const toolId = entry.toolId || entry.id;
  if (!toolId) return null;
  return {
    id: entry.id || toolId,
    toolId,
    name: entry.name || entry.label || toolId,
    route: entry.route || `/calculator/${toolId}`,
    type: entry.type || "calculator",
    lastUsedAt: entry.lastUsedAt || "",
    usageCount: Number(entry.usageCount || 1),
  };
};

export const normalizeRecentTools = (recentTools = []) =>
  (recentTools || []).map(normalizeRecentTool).filter(Boolean);

export const recordRecentTool = (uid, toolOrId, metadata = {}) => {
  const base =
    typeof toolOrId === "object"
      ? { ...toolOrId, ...metadata }
      : { ...metadata, toolId: toolOrId, id: toolOrId };
  const normalized = normalizeRecentTool(base);
  if (!normalized?.toolId) return DEFAULT_WORKSPACE_PREFERENCES;

  const current = loadWorkspacePreferences(uid);
  const now = new Date().toISOString();
  const currentRecentTools = normalizeRecentTools(current.recentTools);
  const existing = currentRecentTools.find((item) => item.toolId === normalized.toolId || item.id === normalized.id);
  const nextActivity = {
    ...existing,
    ...normalized,
    id: normalized.id || normalized.toolId,
    lastUsedAt: now,
    usageCount: (existing?.usageCount || 0) + 1,
  };
  const next = {
    ...current,
    recentTools: [
      nextActivity,
      ...currentRecentTools.filter((item) => item.toolId !== nextActivity.toolId && item.id !== nextActivity.id),
    ].slice(0, 12),
  };
  return saveWorkspacePreferences(uid, next);
};

export const calculateWorkspaceScore = (preferences, visibleCategoryCount, totalCategoryCount) => {
  const favoritesCount = preferences.favorites?.length || 0;
  const hiddenCount = preferences.hiddenCategories?.length || 0;
  const recentCount = preferences.recentTools?.length || 0;
  const workflowCount = preferences.completedWorkflows?.length || 0;
  const healthChecks = preferences.healthChecks || 0;
  const visibleRatio = totalCategoryCount > 0 ? visibleCategoryCount / totalCategoryCount : 1;

  const sortScore = Math.min(100, Math.round((favoritesCount >= 4 ? 45 : favoritesCount * 11) + Math.min(35, hiddenCount * 7) + 20));
  const setScore = Math.min(100, Math.round(Math.min(45, recentCount * 5) + Math.min(35, workflowCount * 12) + (visibleRatio > 0.35 ? 20 : 10)));
  const shineScore = Math.min(100, Math.round(Math.min(50, healthChecks * 20) + Math.min(25, preferences.formulaViews || 0) + (preferences.mode === "advanced" ? 25 : 18)));

  return {
    sortScore,
    setScore,
    shineScore,
    overallScore: Math.round((sortScore + setScore + shineScore) / 3),
  };
};
